import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useCallback, useEffect, useState, ReactNode } from "react";
import { FileAttachment } from "../../../models/Message";
import { RoomItem } from "../../../models/RoomItem";
import { UtilApp } from "../../../services/Utils/UtilApp";
import { ImageViewerModal } from "../../components/MessageFileItem";
import { My } from "../../../services/My";
import { getDisplayName } from "../../../services/Utils/userDisplayName";
import { Room } from "../../../models/Room";
import { SectionCard, SectionActionButton } from "./SectionCard";

export interface IPanelListImages {
  room: Room,
  items: RoomItem[],
  buttons: ReactNode | null,
}

// 우측 패널 고정 폭(304px, w-[304px] flex-shrink-0)에 맞춰 4열 × 2행 = 8개 표시. 초과 시 마지막 셀에 "+N".
const MAX_VISIBLE = 8;

export const PanelListImages: React.FC<IPanelListImages> = ({ room, items, buttons }) => {
  useLingui();
  const [imageList, setImageList] = useState<{ item: RoomItem, file: FileAttachment }[]>([]);
  const [currentImage, setCurrentImage] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  useEffect(() => {
    const _list = items.map(i => {
      const f = new FileAttachment(`${getDisplayName(i.user)}, ${UtilApp.dateStrToDay(i.created, My.user.lang)}`, i.url ?? '', 0, 'image');
      return { item: i, file: f };
    });
    setImageList(_list);
  }, [items])

  const openImageViewer = useCallback((index: number) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  // 표시할 이미지 (4열 × 2행 = 8개) — 초과분만 마지막 셀에 "+N"
  const visibleImages = imageList.slice(0, MAX_VISIBLE);
  const remainingCount = imageList.length - MAX_VISIBLE;
  const fileUrls = imageList.map(m => m.file.url);

  if (imageList.length === 0) {
    return null;
  }

  return (
    <SectionCard
      title={t`Images`}
      count={imageList.length}
      collapsible={false}
      actions={buttons}
    >
      {/* 4열 × 2행 그리드 — 우측 패널 고정 폭(304px)에 최적화 (셀을 채우는 정사각 타일) */}
      <div className="grid grid-cols-4 gap-2">
        {visibleImages.map((o, index) => {
          const isLast = index === visibleImages.length - 1 && remainingCount > 0;
          return (
            <div
              key={o.file.url}
              className="relative aspect-square w-full rounded overflow-hidden bg-muted cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => openImageViewer(index)}
            >
              <img
                src={o.file.url}
                alt={o.file.filename}
                className="w-full h-full object-contain object-center"
              />
              {/* 더 있을 경우 마지막 셀에 오버레이로 표시 */}
              {isLast && (
                <div className="absolute inset-0 bg-background/60 flex items-center justify-center hover:bg-background/70 transition-colors">
                  <span className="text-on-media-text font-semibold text-lg">+{remainingCount}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 이미지 뷰어 모달 */}
      {isViewerOpen && (
        <ImageViewerModal
          images={fileUrls}
          currentImage={currentImage}
          onClose={closeImageViewer}
        />
      )}
    </SectionCard>
  );
}